/**
 * Adjustments Load Manager
 * Subscription-based progress tracking for batch adjustments loading
 * Handles parallel loading across multiple runs and adjustment types
 */

import type { LoadSEERApiClient } from '../api';
import type { AdjustmentDto } from '../types';

export interface AdjustmentsLoadParams {
  scenarioId: number;
  adjustmentTypes: Array<'spatial' | 'user' | 'newGrowth'>;
  runIds: number[]; // For spatial and user only
}

export interface AdjustmentLoadSummary {
  scenarioName: string;
  totalLoaded: number;
  bySources: {
    spatial: number;
    user: number;
    newGrowth: number;
  };
  excludedNoLocation: number;
  duplicatesRemoved: number;
}

export interface LoadProgressSnapshot {
  current: number; // Completed requests
  total: number; // Total requests to make
  completed: number; // Successfully completed
  failed: number; // Failed requests
  phase: 'loading' | 'processing' | 'complete' | 'error';
  currentSource?: string; // e.g., "Spatial (Run 1)"
}

type ProgressCallback = (snapshot: LoadProgressSnapshot) => void;
type CompleteCallback = (adjustments: AdjustmentDto[], summary: AdjustmentLoadSummary) => void;

interface RequestSpec {
  type: 'spatial' | 'user' | 'newGrowth';
  runId?: number;
  label: string;
}

export class AdjustmentsLoadManager {
  private apiClient: LoadSEERApiClient;
  private progressSubscribers = new Set<ProgressCallback>();
  private completeSubscribers = new Set<CompleteCallback>();
  private abortController: AbortController | null = null;
  private isLoading = false;

  constructor(apiClient: LoadSEERApiClient) {
    this.apiClient = apiClient;
  }

  /**
   * Subscribes to progress updates
   * Returns an unsubscribe function
   */
  subscribeProgress(callback: ProgressCallback): () => void {
    this.progressSubscribers.add(callback);
    return () => {
      this.progressSubscribers.delete(callback);
    };
  }

  /**
   * Subscribes to completion events
   * Returns an unsubscribe function
   */
  subscribeComplete(callback: CompleteCallback): () => void {
    this.completeSubscribers.add(callback);
    return () => {
      this.completeSubscribers.delete(callback);
    };
  }

  /**
   * Notifies all progress subscribers
   */
  private notifyProgress(snapshot: LoadProgressSnapshot): void {
    this.progressSubscribers.forEach(callback => {
      try {
        callback(snapshot);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Error in progress callback:', error);
        }
      }
    });
  }

  /**
   * Notifies all complete subscribers
   */
  private notifyComplete(adjustments: AdjustmentDto[], summary: AdjustmentLoadSummary): void {
    this.completeSubscribers.forEach(callback => {
      try {
        callback(adjustments, summary);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Error in complete callback:', error);
        }
      }
    });
  }

  /**
   * Cancels the currently running load operation
   */
  cancel(): void {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  /**
   * Loads adjustments for the specified scenario, runs, and types
   * Handles parallel requests, pagination, deduplication, and progress tracking
   */
  async loadAdjustments(params: AdjustmentsLoadParams): Promise<AdjustmentDto[]> {
    if (this.isLoading) {
      throw new Error('Load operation already in progress');
    }

    this.isLoading = true;
    this.abortController = new AbortController();

    try {
      // Fetch scenario details for summary
      const scenariosResponse = await this.apiClient.getScenarios({ page: 1, pageSize: 1000 });
      const scenario = scenariosResponse.results.find(s => s.id === params.scenarioId);
      const scenarioName = scenario?.name || `Scenario ${params.scenarioId}`;

      // Build request matrix
      const requests = this.buildRequestMatrix(params);

      this.notifyProgress({
        current: 0,
        total: requests.length,
        completed: 0,
        failed: 0,
        phase: 'loading',
      });

      // Execute all requests in parallel
      const allResults = await Promise.allSettled(
        requests.map(req => this.loadSingleSource(req, params.scenarioId))
      );

      // Aggregate results
      const adjustmentsBySource: {
        spatial: AdjustmentDto[];
        user: AdjustmentDto[];
        newGrowth: AdjustmentDto[];
      } = {
        spatial: [],
        user: [],
        newGrowth: [],
      };
      let completedCount = 0;
      let failedCount = 0;

      allResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const type = requests[index].type;
          adjustmentsBySource[type].push(...result.value);
          completedCount++;
        } else {
          failedCount++;
          if (import.meta.env.DEV) {
            console.error(`Failed to load ${requests[index].label}:`, result.reason);
          }
        }
      });

      // If all requests failed, throw error
      if (completedCount === 0) {
        throw new Error('All adjustment requests failed');
      }

      // Merge and deduplicate
      const allAdjustments = [
        ...adjustmentsBySource.spatial,
        ...adjustmentsBySource.user,
        ...adjustmentsBySource.newGrowth,
      ];

      const { deduplicated, duplicatesRemoved } = this.deduplicateAdjustments(allAdjustments);

      // Count adjustments without location for summary
      const excludedNoLocation = deduplicated.filter(
        adj =>
          !adj.location || typeof adj.location.x !== 'number' || typeof adj.location.y !== 'number'
      ).length;

      // Build summary
      const summary: AdjustmentLoadSummary = {
        scenarioName,
        totalLoaded: deduplicated.length,
        bySources: {
          spatial: adjustmentsBySource.spatial.length,
          user: adjustmentsBySource.user.length,
          newGrowth: adjustmentsBySource.newGrowth.length,
        },
        excludedNoLocation,
        duplicatesRemoved,
      };

      this.notifyProgress({
        current: completedCount + failedCount,
        total: requests.length,
        completed: completedCount,
        failed: failedCount,
        phase: 'complete',
      });

      this.notifyComplete(deduplicated, summary);

      return deduplicated;
    } finally {
      this.isLoading = false;
      this.abortController = null;
    }
  }

  /**
   * Builds the request matrix for (run × type) combinations
   */
  private buildRequestMatrix(params: AdjustmentsLoadParams): RequestSpec[] {
    const requests: RequestSpec[] = [];

    for (const type of params.adjustmentTypes) {
      if (type === 'newGrowth') {
        // New Growth has no run dimension
        requests.push({
          type: 'newGrowth',
          label: 'New Growth',
        });
      } else {
        // Spatial and User adjustments per run
        for (const runId of params.runIds) {
          requests.push({
            type,
            runId,
            label: `${type === 'spatial' ? 'Spatial' : 'User'} (Run ${runId})`,
          });
        }
      }
    }

    return requests;
  }

  /**
   * Loads adjustments from a single source with automatic pagination
   */
  private async loadSingleSource(
    request: RequestSpec,
    scenarioId: number
  ): Promise<AdjustmentDto[]> {
    const allResults: AdjustmentDto[] = [];
    let page = 1;
    const pageSize = 100;
    let hasMore = true;

    while (hasMore) {
      if (this.abortController?.signal.aborted) {
        throw new Error('Load cancelled');
      }

      this.notifyProgress({
        current: 0,
        total: 1,
        completed: 0,
        failed: 0,
        phase: 'loading',
        currentSource: `${request.label} (page ${page})`,
      });

      let response;

      if (request.type === 'spatial') {
        response = await this.apiClient.getSpatialAdjustments({
          scenarioId,
          runId: request.runId,
          page,
          pageSize,
        });
      } else if (request.type === 'user') {
        response = await this.apiClient.getUserAdjustments({
          scenarioId,
          runId: request.runId,
          page,
          pageSize,
        });
      } else {
        // newGrowth
        response = await this.apiClient.getNewGrowthAdjustments({
          page,
          pageSize,
        });
      }

      allResults.push(...response.results);

      // Check if there's another page
      hasMore = response.results.length === pageSize && response.totalResults > page * pageSize;
      page++;
    }

    return allResults;
  }

  /**
   * Deduplicates adjustments by adjustmentId (keep first occurrence)
   */
  private deduplicateAdjustments(adjustments: AdjustmentDto[]): {
    deduplicated: AdjustmentDto[];
    duplicatesRemoved: number;
  } {
    const seen = new Set<number>();
    const deduplicated: AdjustmentDto[] = [];

    for (const adj of adjustments) {
      if (adj.adjustmentId === null || !seen.has(adj.adjustmentId)) {
        if (adj.adjustmentId !== null) {
          seen.add(adj.adjustmentId);
        }
        deduplicated.push(adj);
      }
    }

    return {
      deduplicated,
      duplicatesRemoved: adjustments.length - deduplicated.length,
    };
  }
}
